import { useState } from "react"

import { Col, Container, Modal, Row, Tab, Tabs } from "react-bootstrap"
import { toast } from "react-toastify"
import { finalize } from "rxjs/operators"

import OrphanedRow from "../../../api/OrphanedResults/Models/OrphanedRow"
import PendingClinicalTest from "../../../api/OrphanedResults/Models/PendingClinicalTest"
import PendingDrugTest from "../../../api/OrphanedResults/Models/PendingDrugTest"
import {
  NoMatchingCreateNewObservable,
  NoMatchingTestAcceptClinicalObservable,
  NoMatchingTestAcceptObservable,
} from "../../../api/OrphanedResults/ResultExceptionsProcess"
import FormatError from "../../../formatters/FormatError"
import { DraggableModalDialog } from "../../Randomization/GovPools/RandomSchedulingExclusionsModal"
import PendingClinicalTests from "../PendingClinicalTests/PendingClinicalTests"
import PendingClinicalTestsButtons from "../PendingClinicalTests/PendingClinicalTestsButtons"
import PendingDrugTests from "../PendingDrugTests/PendingDrugTests"
import PendingDrugTestsButtons from "../PendingDrugTests/PendingDrugTestsButtons"

interface NoMatchingTestErrorProps {
  errorRow: OrphanedRow
  onClose: (withUpdate: boolean) => void
}
const DRUG_TESTS_TAB = "drugTestsTab"
const CLINICAL_TESTS_TAB = "clinicalTestsTab"

function NoMatchingTestError(props: NoMatchingTestErrorProps) {
  const { errorRow, onClose } = props

  const [selectedTest, setSelectedTest] = useState<PendingDrugTest>()
  const [acceptProc, setAcceptProc] = useState(false)
  const [createNewProc, setCreateNewProc] = useState(false)
  const [selectedClinicalTest, setSelectedClinicalTest] = useState<PendingClinicalTest>()
  const [acceptClinicalProc, setAcceptClinicalProc] = useState(false)
  const [createNewClinicalProc, setCreateNewClinicalProc] = useState(false)

  const [currentTab, setCurrentTab] = useState(DRUG_TESTS_TAB)

  const onCancel = () => {
    onClose(false)
  }

  const onAccept = () => {
    if (!selectedTest) {
      return
    }
    setAcceptProc(true)
    NoMatchingTestAcceptObservable(errorRow, selectedTest)
      .pipe(finalize(() => setAcceptProc(false)))
      .subscribe(
        (data) => {
          toast.success(data)
          onClose(true)
        },
        (error) => toast.error(FormatError(error))
      )
  }

  const onCreateNew = () => {
    setCreateNewProc(true)
    NoMatchingCreateNewObservable(errorRow)
      .pipe(finalize(() => setCreateNewProc(false)))
      .subscribe(
        (data) => {
          toast.success(data)
          onClose(true)
        },
        (error) => toast.error(FormatError(error))
      )
  }

  const onCancelClinical = () => {
    onClose(false)
  }

  const onAcceptClinical = () => {
    if (!selectedClinicalTest) {
      return
    }
    setAcceptClinicalProc(true)
    NoMatchingTestAcceptClinicalObservable(errorRow, selectedClinicalTest)
      .pipe(finalize(() => setAcceptClinicalProc(false)))
      .subscribe(
        (data) => {
          toast.success(data)
          onClose(true)
        },
        (error) => toast.error(FormatError(error))
      )
  }

  const onCreateNewClinical = () => {
    setCreateNewClinicalProc(true)
    NoMatchingCreateNewObservable(errorRow)
      .pipe(finalize(() => setCreateNewClinicalProc(false)))
      .subscribe(
        (data) => {
          toast.success(data)
          onClose(true)
        },
        (error) => toast.error(FormatError(error))
      )
  }

  const showDrugTestsTab = currentTab === DRUG_TESTS_TAB
  const showClinicalTestsTab = currentTab === CLINICAL_TESTS_TAB

  return (
    <Modal dialogAs={DraggableModalDialog} backdrop="static" show size="xl" dialogClassName="dark" onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>No Matching Test Error</Modal.Title>
      </Modal.Header>
      <Modal.Body className="mb-0">
        <Row>
          <Col>Review the current Donors Pending tests and determine if there is a match or create a new test.</Col>
        </Row>
        <Container className="tab-container bg-transparent p-0">
          <Tabs
            defaultActiveKey={DRUG_TESTS_TAB}
            activeKey={currentTab}
            className="mb-2 border-0 px-0"
            onSelect={(eventKey) => !!eventKey && setCurrentTab(eventKey)}
          >
            <Tab eventKey={DRUG_TESTS_TAB} title="Pending Drug Tests" tabClassName="rounded-pill px-4">
              {showDrugTestsTab && (
                <PendingDrugTests
                  donorId={errorRow.PersonID}
                  importTest={errorRow}
                  showTitle={false}
                  selectedTest={selectedTest}
                  onSelectTest={setSelectedTest}
                />
              )}
            </Tab>
            <Tab eventKey={CLINICAL_TESTS_TAB} title="Pending Clinical Tests" tabClassName="rounded-pill px-4">
              {showClinicalTestsTab && (
                <PendingClinicalTests
                  donorId={errorRow.PersonID}
                  showTitle={false}
                  selectedTest={selectedClinicalTest}
                  onSelectTest={setSelectedClinicalTest}
                />
              )}
            </Tab>
          </Tabs>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        {showDrugTestsTab && (
          <PendingDrugTestsButtons
            selectedTest={selectedTest}
            disabled={createNewProc || acceptProc}
            acceptSpin={acceptProc}
            createNewSpin={createNewProc}
            onCancel={onCancel}
            onAccept={onAccept}
            onCreateNew={onCreateNew}
          />
        )}
        {showClinicalTestsTab && (
          <PendingClinicalTestsButtons
            selectedTest={selectedClinicalTest}
            disabled={createNewClinicalProc || acceptClinicalProc}
            acceptSpin={acceptClinicalProc}
            createNewSpin={createNewClinicalProc}
            onCancel={onCancelClinical}
            onAccept={onAcceptClinical}
            onCreateNew={onCreateNewClinical}
          />
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default NoMatchingTestError
